import { useEffect, useState } from 'react';
import PageHeader1 from '../../components/common/PageHeader1';
import EditButton from "../../components/common/EditButton";
import DeleteButton from "../../components/common/DeleteButton";
import apiClient from "../../utils/api";
import { toast } from 'react-toastify';
import { Pagination } from 'react-bootstrap';

function IncidentsList() {
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [perPage] = useState(10);

    const fetchIncidents = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/incidents?page=${currentPage}&per_page=${perPage}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIncidents(response.data.data);
            setTotalPages(Math.ceil(response.data.total / response.data.per_page));
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to fetch incidents');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIncidents();
    }, [currentPage]); // Re-fetch when page changes

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this incident?')) {
            try {
                const token = localStorage.getItem('auth_token');
                await apiClient.delete(`accounts/incidents/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Incident deleted successfully');
                fetchIncidents();
            } catch (error) {
                console.error('Error:', error);
                toast.error('Failed to delete incident');
            }
        }
    };

    const handlePrint = (incident) => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Incident Report #${incident.id}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        .header { text-align: center; margin-bottom: 30px; }
                        .content { margin: 20px 0; }
                        .label { font-weight: bold; margin-right: 10px; }
                        .info-row { margin: 10px 0; }
                        .images { margin-top: 20px; }
                        .image { max-width: 300px; margin: 10px; }
                        .approval { margin-top: 20px; padding: 10px; border-top: 1px solid #ccc; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2>Incident Report #${incident.id}</h2>
                    </div>
                    <div class="content">
                        <div class="info-row">
                            <span class="label">Car Plate:</span>
                            <span>${incident.car_plate}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Driver:</span>
                            <span>${incident.driver_name}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Date & Time:</span>
                            <span>${new Date(incident.date_time).toLocaleString()}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Description:</span>
                            <p>${incident.incident_description}</p>
                        </div>
                        <div class="images">
                            <span class="label">Images:</span><br/>
                            ${incident.images.map(img => `
                                <img src="${process.env.REACT_APP_STORAGE_URL}${img.url}" alt="${img.file_name}" class="image"/>
                            `).join('')}
                        </div>
                        <div class="approval">
                            <span class="label">Supervisor Approval:</span>
                            <span>${incident.supervisor_approval ? 'Approved' : 'Pending'}</span>
                        </div>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <div className="body d-flex py-3">
            <div className="container-xxl">
                <PageHeader1 
                    pagetitle='Incidents List' 
                    righttitle='Report Incident' 
                    link='/accounts/incidents-add' 
                    routebutton={true} 
                    permission="incidents.create"
                />
                <div className="row g-3 mb-3">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead>
                                            <tr>
                                                <th>Id</th>
                                                <th>Car Plate</th>
                                                <th>Driver</th>
                                                <th>Date & Time</th>
                                                <th>Supervisor Approval</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {incidents.map((incident) => (
                                                <tr key={incident.id}>
                                                    <td>#{incident.id}</td>
                                                    <td>{incident.car_plate}</td>
                                                    <td>{incident.driver_name}</td>
                                                    <td>{incident.created_at}</td>
                                                    <td>
                                                        <span className={`badge ${incident.supervisor_approval ? 'bg-success' : 'bg-warning'}`}>
                                                            {incident.supervisor_approval ? 'Approved' : 'Pending'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="btn-group">
                                                            <button 
                                                                className="btn btn-primary"
                                                                onClick={() => handlePrint(incident)}
                                                            >
                                                                Print
                                                            </button>
                                                            <EditButton
                                                                to={`${process.env.PUBLIC_URL}/accounts/incidents-edit/${incident.id}`}
                                                                permission="incidents.edit"
                                                            />
                                                            <DeleteButton
                                                                permission="incidents.delete"
                                                                onDelete={() => handleDelete(incident.id)}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex justify-content-center mt-3">
                                    <Pagination>
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)} 
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)} 
                                            disabled={currentPage === 1}
                                        />
                                        
                                        {[...Array(totalPages)].map((_, idx) => (
                                            <Pagination.Item
                                                key={idx + 1}
                                                active={idx + 1 === currentPage}
                                                onClick={() => handlePageChange(idx + 1)}
                                            >
                                                {idx + 1}
                                            </Pagination.Item>
                                        ))}
                                        
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)} 
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)} 
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default IncidentsList;